import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { userAPI } from '../services/apiService';
import { toast } from 'sonner';

const Transactions = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    loadTransactions();
  }, [navigate]);

  const loadTransactions = async () => {
    try {
      const data = await userAPI.getTransactions();
      setTransactions(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Confirmed': { variant: 'default', className: 'bg-green-100 text-green-800 hover:bg-green-100' },
      'Pending': { variant: 'secondary', className: 'bg-orange-100 text-orange-800 hover:bg-orange-100' },
      'Rejected': { variant: 'destructive', className: 'bg-red-100 text-red-800 hover:bg-red-100' },
      'Paid': { variant: 'default', className: 'bg-blue-100 text-blue-800 hover:bg-blue-100' }
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    return (
      <Badge variant={config.variant} className={config.className}>
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading transactions...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Calculate totals from transactions
  const totalBalance = user?.balance || 0;
  const pendingBalance = user?.pendingBalance || 0;
  const lifetimeEarnings = user?.lifetimeEarnings || 0;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Transactions & Payouts</h1>
          <p className="text-gray-700 text-lg font-medium">View your complete sales and payout history</p>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-3 border-red-400 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Available Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">₹{totalBalance.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-yellow-400 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Pending Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-600">₹{pendingBalance.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-1">In return window</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-pink-400 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Lifetime Earnings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">₹{lifetimeEarnings.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Transactions Table */}
        <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions && transactions.length > 0 ? (
                    transactions.map((transaction) => (
                    <TableRow key={transaction._id || transaction.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        {new Date(transaction.createdAt || transaction.date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-medium">
                          {transaction.type || 'Sale'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{transaction.orderId || 'N/A'}</TableCell>
                      <TableCell>{transaction.customerName || 'N/A'}</TableCell>
                      <TableCell className="max-w-xs truncate">{transaction.product || 'N/A'}</TableCell>
                      <TableCell className="text-right font-semibold">
                        ₹{(transaction.amount || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className={(transaction.commission || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {(transaction.commission || 0) >= 0 ? '+' : ''}₹{(transaction.commission || 0).toLocaleString()}
                        </span>
                        {transaction.commissionRate > 0 && (
                          <span className="text-xs text-gray-500 ml-1">({transaction.commissionRate}%)</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                    </TableRow>
                  ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No transactions found. Start referring customers to earn commissions!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> Commissions move from Pending → Confirmed after the returns window (usually 30 days). 
                You can request a payout when your available balance reaches ₹1,000.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Transactions;
